import { useMemo } from "react";

import { useLocalization } from "@/hooks";
import { UseCase } from "@/types/utils";

export type UseCaseConfigField = "cells_per_respondent" | "items_per_cell";

export interface UseCaseRequirements {
  /** Editable rotation settings (config block), in display order. */
  config_fields: UseCaseConfigField[];
  /** Inclusive bounds for a given rotation field. */
  field_bounds: Partial<
    Record<UseCaseConfigField, { max: number; min: number }>
  >;
  /** Inclusive max number of cells. */
  max_cells: number;
  /** Inclusive min number of cells required to run the study. */
  min_cells: number;
}

const NONE: UseCaseRequirements = {
  config_fields: [],
  field_bounds: {},
  max_cells: 0,
  min_cells: 0,
};

/**
 * Per-use-case section requirements. Mirrors the backend `TEMPLATES` registry
 * but only for the editor's UI/validation needs (cell bounds, which rotation
 * knobs are editable, defaults). Kept as a local switch — same pattern as
 * Question.tsx — to avoid round-tripping static data through an API.
 */
export const getUseCaseRequirements = (
  useCase: UseCase | null
): UseCaseRequirements => {
  switch (useCase) {
    /* Preference Test, Awareness, MaxDiff and TURF author their item pool as
       the questions' options (no section cells), so the section itself has no
       cell requirements. */
    case UseCase.PreferenceTest:
      return NONE;
    case UseCase.AdCreative:
      return {
        ...NONE,
        config_fields: ["cells_per_respondent"],
        field_bounds: { cells_per_respondent: { max: 20, min: 1 } },
        max_cells: 5,
        min_cells: 1,
      };
    case UseCase.Awareness:
      return NONE;
    case UseCase.ClaimTest:
      return {
        ...NONE,
        config_fields: ["cells_per_respondent"],
        field_bounds: { cells_per_respondent: { max: 20, min: 1 } },
        max_cells: 5,
        min_cells: 1,
      };
    case UseCase.MaxDiff:
      return NONE;
    case UseCase.Monadic:
      return {
        ...NONE,
        config_fields: ["cells_per_respondent"],
        field_bounds: { cells_per_respondent: { max: 20, min: 1 } },
        max_cells: 20,
        min_cells: 1,
      };
    case UseCase.NPS:
      return {
        ...NONE,
        /* Up to 5 products per NPS section. Each persona is shown ONE
           product (monadic rotation: `cells_per_respondent=1`,
           `items_per_cell=1`) — keeps the loyalty signal clean and
           lets run-level rotation balance coverage across products.
           Rotation isn't user-tunable (it's always 1/1), so we omit
           `config_fields`. */
        max_cells: 5,
        min_cells: 1,
      };
    case UseCase.Packaging:
      return {
        ...NONE,
        config_fields: ["cells_per_respondent"],
        field_bounds: { cells_per_respondent: { max: 20, min: 1 } },
        max_cells: 5,
        min_cells: 1,
      };
    case UseCase.PriceElasticity:
      return {
        ...NONE,
        config_fields: ["cells_per_respondent"],
        field_bounds: { cells_per_respondent: { max: 20, min: 1 } },
        max_cells: 5,
        min_cells: 1,
      };
    case UseCase.TURF:
      return NONE;
    default:
      return NONE;
  }
};

/* ── Per-use-case copy ────────────────────────────────────────────────
 *
 * Section-editor labels diverge per use case ("Variants" vs "Brands" vs
 * "Concepts" vs ...) and the i18next-scanner can ONLY see literal
 * `i18n.t("...")` arguments — template literals or computed keys are
 * invisible to it. So this helper expands every use case as its own
 * branch with full literal keys, paying a verbose-once tax to keep
 * static extraction working.
 *
 * The shape is uniform so the editor doesn't have to special-case
 * "field X exists for this use case" — getters for fields that aren't
 * shown for a given use case (e.g. `cellsPerRespondent` on Awareness)
 * still fall back to the generic key, which is harmless because the
 * editor reads those getters only when `requirements.<gate>` is true.
 */
export interface UseCaseLabels {
  addStimulus: string;
  cellsPerRespondent: string;
  cellsPerRespondentHint: (count: number) => string;
  itemsPerCell: string;
  itemsPerCellHint: (count: number) => string;
  requiresMediaHint: string;
  stimuli: string;
  stimulusPlaceholder: (index: number) => string;
  summaryCells: (count: number) => string;
  summaryItems: (count: number) => string;
  summaryNoStimuli: string;
  summaryStimuli: (count: number) => string;
}

/**
 * Returns the localized, use-case-aware labels for the section editor.
 * Pass `null` (or omit `useCase` on a section) to get the generic copy
 * used for blank/custom sections. Memoised on the active `i18n` and the
 * `useCase`, so callers can just `const labels = useUseCaseLabels(...)`
 * without wrapping a `useMemo` of their own.
 */
export const useUseCaseLabels = (useCase: UseCase | null): UseCaseLabels => {
  const { i18n } = useLocalization();
  return useMemo<UseCaseLabels>(() => {
    /* Fallbacks for fields a given use case doesn't override. Keeping the
       full uniform shape lets the caller treat every field the same way. */
    const generic: UseCaseLabels = {
      addStimulus: i18n.t("studies.sectionEditor.addStimulus"),
      cellsPerRespondent: i18n.t("studies.sectionEditor.cellsPerRespondent"),
      cellsPerRespondentHint: (count) =>
        i18n.t("studies.sectionEditor.cellsPerRespondentHint", { count }),
      itemsPerCell: i18n.t("studies.sectionEditor.itemsPerCell"),
      itemsPerCellHint: (count) =>
        i18n.t("studies.sectionEditor.itemsPerCellHint", { count }),
      requiresMediaHint: i18n.t("studies.sectionEditor.requiresMediaHint"),
      stimuli: i18n.t("studies.sectionEditor.stimuli"),
      stimulusPlaceholder: (index) =>
        i18n.t("studies.sectionEditor.stimulusPlaceholder", { index }),
      summaryCells: (count) =>
        i18n.t("studies.sectionEditor.summaryCells", { count }),
      summaryItems: (count) =>
        i18n.t("studies.sectionEditor.summaryItems", { count }),
      summaryNoStimuli: i18n.t("studies.sectionEditor.summaryNoStimuli"),
      summaryStimuli: (count) =>
        i18n.t("studies.sectionEditor.summaryStimuli", { count }),
    };

    /* Shared label set for use cases whose stimuli are "products" — PE,
       TURF, NPS. Lives once at `studies.sectionEditor.products.*` so
       these three don't carry identical copies of the same strings. */
    const products = {
      addStimulus: i18n.t("studies.sectionEditor.products.addStimulus"),
      stimuli: i18n.t("studies.sectionEditor.products.stimuli"),
      stimulusPlaceholder: (index: number) =>
        i18n.t("studies.sectionEditor.products.stimulusPlaceholder", { index }),
      summaryNoStimuli: i18n.t(
        "studies.sectionEditor.products.summaryNoStimuli"
      ),
      summaryStimuli: (count: number) =>
        i18n.t("studies.sectionEditor.products.summaryStimuli", { count }),
    };

    switch (useCase) {
      case UseCase.PreferenceTest:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.preference_test.addStimulus"
          ),
          stimuli: i18n.t(
            "studies.sectionEditor.useCase.preference_test.stimuli"
          ),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.preference_test.stimulusPlaceholder",
              {
                index,
              }
            ),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.preference_test.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.preference_test.summaryStimuli",
              {
                count,
              }
            ),
        };
      case UseCase.AdCreative:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.ad_creative.addStimulus"
          ),
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.ad_creative.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.ad_creative.cellsPerRespondentHint",
              { count }
            ),
          requiresMediaHint: i18n.t(
            "studies.sectionEditor.useCase.ad_creative.requiresMediaHint"
          ),
          stimuli: i18n.t("studies.sectionEditor.useCase.ad_creative.stimuli"),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.ad_creative.stimulusPlaceholder",
              { index }
            ),
          summaryCells: (count) =>
            i18n.t("studies.sectionEditor.useCase.ad_creative.summaryCells", {
              count,
            }),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.ad_creative.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t("studies.sectionEditor.useCase.ad_creative.summaryStimuli", {
              count,
            }),
        };
      case UseCase.Awareness:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.awareness.addStimulus"
          ),
          stimuli: i18n.t("studies.sectionEditor.useCase.awareness.stimuli"),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.awareness.stimulusPlaceholder",
              { index }
            ),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.awareness.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t("studies.sectionEditor.useCase.awareness.summaryStimuli", {
              count,
            }),
        };
      case UseCase.ClaimTest:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.claim_test.addStimulus"
          ),
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.claim_test.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.claim_test.cellsPerRespondentHint",
              { count }
            ),
          stimuli: i18n.t("studies.sectionEditor.useCase.claim_test.stimuli"),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.claim_test.stimulusPlaceholder",
              { index }
            ),
          summaryCells: (count) =>
            i18n.t("studies.sectionEditor.useCase.claim_test.summaryCells", {
              count,
            }),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.claim_test.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t("studies.sectionEditor.useCase.claim_test.summaryStimuli", {
              count,
            }),
        };
      case UseCase.MaxDiff:
        return {
          ...generic,
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.max_diff.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.max_diff.cellsPerRespondentHint",
              { count }
            ),
          itemsPerCell: i18n.t(
            "studies.sectionEditor.useCase.max_diff.itemsPerCell"
          ),
          itemsPerCellHint: (count) =>
            i18n.t("studies.sectionEditor.useCase.max_diff.itemsPerCellHint", {
              count,
            }),
          summaryCells: (count) =>
            i18n.t("studies.sectionEditor.useCase.max_diff.summaryCells", {
              count,
            }),
          summaryItems: (count) =>
            i18n.t("studies.sectionEditor.useCase.max_diff.summaryItems", {
              count,
            }),
        };
      case UseCase.Monadic:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.monadic.addStimulus"
          ),
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.monadic.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.monadic.cellsPerRespondentHint",
              { count }
            ),
          stimuli: i18n.t("studies.sectionEditor.useCase.monadic.stimuli"),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.monadic.stimulusPlaceholder",
              {
                index,
              }
            ),
          summaryCells: (count) =>
            i18n.t("studies.sectionEditor.useCase.monadic.summaryCells", {
              count,
            }),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.monadic.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t("studies.sectionEditor.useCase.monadic.summaryStimuli", {
              count,
            }),
        };
      case UseCase.Packaging:
        return {
          ...generic,
          addStimulus: i18n.t(
            "studies.sectionEditor.useCase.packaging.addStimulus"
          ),
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.packaging.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.packaging.cellsPerRespondentHint",
              { count }
            ),
          requiresMediaHint: i18n.t(
            "studies.sectionEditor.useCase.packaging.requiresMediaHint"
          ),
          stimuli: i18n.t("studies.sectionEditor.useCase.packaging.stimuli"),
          stimulusPlaceholder: (index) =>
            i18n.t(
              "studies.sectionEditor.useCase.packaging.stimulusPlaceholder",
              { index }
            ),
          summaryCells: (count) =>
            i18n.t("studies.sectionEditor.useCase.packaging.summaryCells", {
              count,
            }),
          summaryNoStimuli: i18n.t(
            "studies.sectionEditor.useCase.packaging.summaryNoStimuli"
          ),
          summaryStimuli: (count) =>
            i18n.t("studies.sectionEditor.useCase.packaging.summaryStimuli", {
              count,
            }),
        };
      case UseCase.NPS:
      case UseCase.TURF:
        return {
          ...generic,
          ...products,
        };
      case UseCase.PriceElasticity:
        return {
          ...generic,
          ...products,
          cellsPerRespondent: i18n.t(
            "studies.sectionEditor.useCase.price_elasticity.cellsPerRespondent"
          ),
          cellsPerRespondentHint: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.price_elasticity.cellsPerRespondentHint",
              { count }
            ),
          summaryCells: (count) =>
            i18n.t(
              "studies.sectionEditor.useCase.price_elasticity.summaryCells",
              { count }
            ),
        };
      default:
        return generic;
    }
  }, [i18n, useCase]);
};
